import { useState, useEffect } from 'react';
import ProductsSection from '../components/ProductsSection';
import HeroSlider from '../components/HeroSlider';
import ProductGrid from '../components/ProductGrid';
import {
  fetchFeaturedProducts,
  fetchOnSaleProducts,
  fetchProducts,
  fetchProductsWithCount,
} from '../utils/apiHelpers';
import type { Product } from '../utils/apiHelpers';

export default function HomePage() {
  const [featured, setFeatured] = useState<Product[]>([]);
  const [loadingFeatured, setLoadingFeatured] = useState(true);
  const [onSale, setOnSale] = useState<Product[]>([]);
  const [loadingOnSale, setLoadingOnSale] = useState(true);
  const [allProducts, setAllProducts] = useState<Product[]>([]);
  const [loadingAll, setLoadingAll] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [allProductsPage, setAllProductsPage] = useState(1);
  const [allProductsCount, setAllProductsCount] = useState(0);

  useEffect(() => {
    let cancelled = false;
    fetchFeaturedProducts(12)
      .then((items) => {
        if (!cancelled) setFeatured(items);
      })
      .catch(() => {
        if (!cancelled) setFeatured([]);
      })
      .finally(() => {
        if (!cancelled) setLoadingFeatured(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    fetchOnSaleProducts(12)
      .then((items) => {
        if (!cancelled) setOnSale(items);
      })
      .catch(() => {
        if (!cancelled) setOnSale([]);
      })
      .finally(() => {
        if (!cancelled) setLoadingOnSale(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoadingAll(true);
    setAllProductsPage(1);

    fetchProductsWithCount({}, 25)
      .then(({ results, count }) => {
        if (!cancelled) {
          setAllProducts(results);
          setAllProductsCount(count);
        }
      })
      .catch(() => {
        if (!cancelled) setAllProducts([]);
      })
      .finally(() => {
        if (!cancelled) setLoadingAll(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const loadMoreProducts = async () => {
    if (loadingMore || allProducts.length >= allProductsCount) return;
    setLoadingMore(true);
    try {
      const nextPage = allProductsPage + 1;
      const moreProducts = await fetchProducts({ page: nextPage, page_size: 25 });
      setAllProducts((prev) => [...prev, ...moreProducts]);
      setAllProductsPage(nextPage);
    } catch {
      // ignore load more failure and keep current products
    } finally {
      setLoadingMore(false);
    }
  };

  return (
    <main>
      <HeroSlider />
      <ProductsSection
        title="Best Featured Products"
        products={featured}
        loading={loadingFeatured}
        viewAllTo="/shop"
      />
      <section className="px-4 py-8">
        <div className="max-w-7xl mx-auto">
          <div className="relative bg-gradient-to-r from-[#fd9e10] via-[#ff8c00] to-[#f08c00] rounded-lg overflow-visible shadow-2xl">
            {/* Neon accent line */}
            <div className="absolute top-0 left-1/3 w-1 h-full bg-gradient-to-b from-[#fd9e10] to-transparent opacity-60"></div>
            
            {/* Decorative neon triangle */}
            <div className="absolute top-0 right-20 w-0 h-0 border-l-24 border-r-24 border-b-48 border-l-transparent border-r-transparent border-b-[#fd9e10] opacity-20"></div>
            
            <div className="relative flex flex-col md:flex-row justify-between items-center px-8 md:px-16 py-1 md:py-1">
              <div className="flex flex-col justify-center items-start md:w-1/2 z-10">
                <h3 className="text-3xl md:text-5xl font-bold text-white mb-1 leading-tight">
                  Shop and <span className="text-cyan-300">SAVE BIG</span>
                </h3>
                <p className="text-xl md:text-2xl text-white font-light mb-6">
                  on hottest deals
                </p>
                <a
                  href="/shop"
                  className="bg-white text-[#fd9e10] px-8 py-3 rounded-lg font-bold hover:bg-gray-100 transition-all duration-300 hover:shadow-lg hover:shadow-[#fd9e10]/50 text-base md:text-lg"
                >
                  Shop Now
                </a>
              </div>
              <div className="hidden md:flex md:w-1/2 justify-end items-center pt-4 md:pt-0 relative">
                <div className="relative z-10">
                  <img 
                    src="/bag.png" 
                    alt="Shopping Bag" 
                    className="h-52 md:h-80 object-contain drop-shadow-2xl transition-transform duration-500 ease-in-out hover:scale-110 hover:drop-shadow-[#fd9e10]"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      <ProductsSection
        title="On Sale"
        products={onSale}
        loading={loadingOnSale}
        viewAllTo="/shop"
        reducedSize={true}
      />
      <section className="px-4 py-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Banner 1 */}
            <div className="relative bg-gradient-to-br from-[#fd9e10] via-[#ff8c00] to-[#f08c00] rounded-lg overflow-visible shadow-xl h-40 md:h-48">
              <div className="absolute inset-0 opacity-30">
                <div className="absolute top-0 left-0 w-96 h-96 bg-white rounded-full -ml-48 -mt-48"></div>
              </div>
              <div className="relative h-full flex flex-col md:flex-row justify-between items-center px-6 md:px-8 py-4 z-10">
                <div className="flex flex-col justify-center items-start md:w-1/2">
                  <h3 className="text-2xl md:text-3xl font-bold text-white mb-2">
                    Tech Deals
                  </h3>
                  <p className="text-sm md:text-base text-white/90 mb-3">
                    Save up to 40% on electronics
                  </p>
                  <a
                    href="/shop"
                    className="bg-white text-[#fd9e10] px-5 py-2 rounded-lg font-semibold text-sm hover:bg-gray-100 transition-all duration-300 hover:shadow-lg"
                  >
                    Explore
                  </a>
                </div>
                <div className="hidden md:flex md:w-1/2 justify-end items-center">
                  <img 
                    src="/camera.png" 
                    alt="Camera" 
                    className="h-32 md:h-40 object-contain drop-shadow-lg transition-transform duration-500 ease-in-out hover:scale-110"
                  />
                </div>
              </div>
            </div>

            {/* Banner 2 */}
            <div className="relative bg-gradient-to-br from-[#fd9e10] via-[#ff8c00] to-[#f08c00] rounded-lg overflow-visible shadow-xl h-40 md:h-48">
              <div className="absolute inset-0 opacity-30">
                <div className="absolute bottom-0 right-0 w-96 h-96 bg-white rounded-full -mr-48 -mb-48"></div>
              </div>
              <div className="relative h-full flex flex-col md:flex-row justify-between items-center px-6 md:px-8 py-4 z-10">
                <div className="flex flex-col justify-center items-start md:w-1/2">
                  <h3 className="text-2xl md:text-3xl font-bold text-white mb-2">
                    Fashion Week
                  </h3>
                  <p className="text-sm md:text-base text-white/90 mb-3">
                    New collection now available
                  </p>
                  <a
                    href="/shop"
                    className="bg-white text-[#fd9e10] px-5 py-2 rounded-lg font-semibold text-sm hover:bg-gray-100 transition-all duration-300 hover:shadow-lg"
                  >
                    Shop Now
                  </a>
                </div>
                <div className="hidden md:flex md:w-1/2 justify-end items-center">
                  <img 
                    src="/laptop.png" 
                    alt="Laptop" 
                    className="h-32 md:h-40 object-contain drop-shadow-lg transition-transform duration-500 ease-in-out hover:scale-110"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      <section className="px-4 py-12">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-bold text-[var(--foreground)]">
              All Products
            </h2>
            <a
              href="/shop"
              className="text-sm text-[#fd9e10] hover:underline"
            >
              View All →
            </a>
          </div>
          <ProductGrid
            products={allProducts}
            loading={loadingAll && allProducts.length === 0}
            onLoadMore={loadMoreProducts}
            hasMore={allProducts.length < allProductsCount}
            loadingMore={loadingMore}
          />
        </div>
      </section>
    </main>
  );
}
